import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

import { GeckoWeld } from './geckoWelds'

export interface GeckoGenericHeadCircumferentialWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.GenericHeadCircumferentialWeld
  feature_geometry: {
    x_start: number
    x_end: number
    y: number
  }
  y_measurement_unit: string
}

export const NewGeckoGenericHeadCircumferentialWeldJSON: GeckoGenericHeadCircumferentialWeldJSON =
  {
    name: 'New Generic Head Circumferential Weld',
    type: GeckoFeatureModelType.GenericHeadCircumferentialWeld,
    feature_geometry: {
      x_start: 0,
      x_end: 0,
      y: 0,
    },
    y_measurement_unit: 'meters',
  }

export class GeckoGenericHeadCircumferentialWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.GenericHeadCircumferentialWeld
  public readonly y_measurement_unit: string

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoGenericHeadCircumferentialWeldJSON
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x_start as number,
      feature_geometry.y as number,
      feature_geometry.x_end as number,
      feature_geometry.y as number
    )
    this.y_measurement_unit = weldJson.y_measurement_unit
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJson: Record<string, GeckoGenericHeadCircumferentialWeldJSON>
  ): Map<string, GeckoGenericHeadCircumferentialWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      const weld = new GeckoGenericHeadCircumferentialWeld(
        componentID,
        weldID,
        recordsJson[weldID]
      )
      resultObj.set(weldID, weld)
      return resultObj
    }, new Map<string, GeckoGenericHeadCircumferentialWeld>())
  }

  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoGenericHeadCircumferentialWeldJSON
  ): GeckoGenericHeadCircumferentialWeld {
    return new GeckoGenericHeadCircumferentialWeld(
      componentID,
      weldID,
      weldJson
    )
  }

  public asJSON() {
    return generateGenericHeadCircumferentialWeld(
      this.name,
      this.feature_geometry.y.min,
      this.feature_geometry.x.min,
      this.feature_geometry.x.max
    )
  }
}

export function generateGenericHeadCircumferentialWeld(
  name: string | undefined,
  y: GeckoMeasurement,
  xStart: GeckoMeasurement,
  xEnd: GeckoMeasurement,
  yMeasurementUnitString: string = 'meters'
): GeckoGenericHeadCircumferentialWeldJSON {
  const yMeasurementUnit =
    yMeasurementUnitString === 'degrees'
      ? GeckoMeasurementUnit.Degrees
      : GeckoMeasurementUnit.Meters

  const GenericHeadCircumferentialWeldJSON: GeckoGenericHeadCircumferentialWeldJSON =
    {
      name: name ?? undefined,
      type: GeckoFeatureModelType.GenericHeadCircumferentialWeld,
      feature_geometry: {
        y: y.convertTo(yMeasurementUnit).toPrecision(assetModelPrecision).num,
        x_start: xStart
          .convertTo(GeckoMeasurementUnit.Degrees)
          .toPrecision(assetModelPrecision).num,
        x_end: xEnd
          .convertTo(GeckoMeasurementUnit.Degrees)
          .toPrecision(assetModelPrecision).num,
      },
      y_measurement_unit: yMeasurementUnitString,
    }

  return GenericHeadCircumferentialWeldJSON
}

export const GenericHeadCircumferentialWeldFormFields = (
  weldJSON: GeckoGenericHeadCircumferentialWeldJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: weldJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_start',
      label: 'X Start',
      value: weldJSON.feature_geometry.x_start,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_end',
      label: 'X End',
      value: weldJSON.feature_geometry.x_end,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_measurement_unit',
      label: 'Y Measurement Unit',
      value: weldJSON.y_measurement_unit,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: [
        { value: 'degrees', label: 'Degrees' },
        { value: 'meters', label: 'Meters' },
      ],
    },
  ]
}

export const GenericHeadCircumferentialWeldJSONFromFormFields = (
  formFields: FormField[]
) => {
  const selectedYUnitField = getFormFieldByDataKey(
    formFields,
    'y_measurement_unit'
  )?.value

  const selectedYUnit =
    selectedYUnitField === 'degrees'
      ? GeckoMeasurementUnit.Degrees
      : GeckoMeasurementUnit.Meters
  return generateGenericHeadCircumferentialWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      selectedYUnit
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x_start')?.value as number,
      GeckoMeasurementUnit.Degrees
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x_end')?.value as number,
      GeckoMeasurementUnit.Degrees
    ),
    selectedYUnitField as string
  )
}

export const GenericHeadCircumferentialWeldFeatureTableOptions: FeatureTableOptions =
  {
    type: GeckoFeatureModelType.GenericHeadCircumferentialWeld,
    title: 'Circumferential Welds',
    singularName: 'Circumferential Weld',
    fields: ['x_start', 'x_end', 'y'],
    sortBy: [{ fieldName: 'y', order: 'asc' }],
    tableMessage:
      'Adding / Editing of Circumferential Welds is disabled as they are automatically generated from Plates and/or Courses',
  }
